import { constants, copy, types } from "../definitions";

/**
 * Formats for MB and KB
 * - MB: '1.0 MB'
 * - KB: '100 KB'
 */
const formatFriendlyFileSize = (file: File): string => {
  // Format size in bytes to MB
  const fileSizeInMb =
    file.size / constants.BYTE_DIVISOR_BINARY / constants.BYTE_DIVISOR_BINARY;

  /**
   * If conversion is < 1, measure in KB
   * Else in MB
   */
  const fileSizeUnit =
    fileSizeInMb < 1
      ? types.FileSizeMeasurement.kb
      : types.FileSizeMeasurement.mb;

  /**
   * If conversion is < 1, times by 1000
   * Else round to nearest tenth
   */
  const fileSizeRounded =
    fileSizeInMb < 1
      ? Math.round(fileSizeInMb * 1000)
      : Math.round(fileSizeInMb * 10) / 10;

  const friendlyFileSize = `${fileSizeRounded} ${fileSizeUnit}`;

  return friendlyFileSize;
};

const validateFileSize = ({
  file,
  maxFileSize,
}: types.FormatFileArgs): types.ValidateFileSizeReturn => {
  // Divide MB by BYTE_DIVISOR_BINARY twice to get to Bytes
  // MB -> KB -> Bytes
  const maxFileSizeToBytes =
    maxFileSize * constants.BYTE_DIVISOR_BINARY * constants.BYTE_DIVISOR_BINARY;

  const isValid = file.size < maxFileSizeToBytes;

  const friendlyFileSize = formatFriendlyFileSize(file);

  return { friendlyFileSize, isValid };
};

const formatFile = ({
  file,
  maxFileSize,
}: types.FormatFileArgs): types.FormatFileReturn => {
  const { isValid, friendlyFileSize } = validateFileSize({ file, maxFileSize });

  const description = isValid
    ? friendlyFileSize
    : copy.errors.fileSize(friendlyFileSize, maxFileSize);

  const variant = isValid
    ? types.PasteFileUploaderItemVariant.default
    : types.PasteFileUploaderItemVariant.error;

  const formattedFile = {
    file,
    description,
    variant,
    title: file.name,
  };

  return {
    isValid,
    formattedFile,
  };
};

export { formatFile, formatFriendlyFileSize, validateFileSize };
